import React from "react";
import { Calendar, Views } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import localizer from "react-big-calendar/lib/localizers/moment";
import moment from "moment";
import I18nProvider from "../../services/I18nProvider";
import Text from "../Text";
import Messages from "../../Shared/Messages";
import { getMobileOperatingSystem } from "../../Common/Onboarding/Utils";
import MPSBarLoader from "../MPSBarLoader";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { store } from "../../State/store/configureStore";
import { showToaster } from "../../State/actions/toaster";

const intl = I18nProvider.getIntlProvider();
let allViews = Object.keys(Views).map((k) => Views[k]);
const globalizeLocalizer = localizer(moment);

// Wrap Calendar with DnD capabilities
const DragAndDropCalendar = withDragAndDrop(Calendar);

class MPSCalendar extends React.PureComponent {
  constructor(props) {
    super(props);
    let minTime = props.minTime;
    if (!minTime) {
      minTime = new Date();
      minTime.setHours(0, 0, 0);
    }

    let maxTime = props.maxTime;
    if (!maxTime) {
      maxTime = new Date();
      maxTime.setHours(23, 0, 0);
    }

    // Set initial date - use appointment start if it exists, otherwise use currentDate or now
    const initialDate = props.appointment?.start
      ? moment(props.appointment.start).toDate()
      : props.currentDate || moment().toDate();

    this.state = {
      minTime,
      maxTime,
      hasNavigated: false,
      initialDate,
      isDragging: false,
    };
  }

  handleNavigate = (date, view, action) => {
    this.setState({ hasNavigated: true });
    this.props.onNavigate(view, date);
  };

  onView = (view) => {
    this.setState({ hasNavigated: true });
    this.props.onNavigate(view, this.props.currentDate || new Date());
  };

  handleSelectSlot = (data) => {
    let { start } = data;
    let { currentView } = this.props;
    if (currentView === Views.DAY) {
      this.setState({
        currentDate: moment(start).toDate(),
        hasNavigated: true,
      });
      this.props.onSelectSlot(data);
    } else {
      let currentDate = moment(start).toDate();
      this.setState({ hasNavigated: true });
      this.props.onNavigate(Views.DAY, currentDate);
    }
  };

  onDrillDown = (date, drilldownView) => {
    this.setState({ hasNavigated: true });
    this.props.onNavigate(drilldownView || Views.DAY, date);
  };

  onShowMore = (events, date, noOfEvents) => {
    let currentDate = moment(date).toDate();
    this.setState({ hasNavigated: true });
    this.props.onNavigate(Views.DAY, currentDate);
  };

  handleSelectEvent = (event) => {
    let { onSelectEvent } = this.props;
    // Don't trigger selection for the current appointment marker
    if (event.isCurrentAppointment) {
      return;
    }
    onSelectEvent(event);
  };

  // Handle drag start to set dragging state
  handleDragStart = ({ event }) => {
    if (event.isCurrentAppointment) {
      this.setState({ isDragging: true });
    }
  };

  // Handle event drop for drag and drop
  handleEventDrop = ({ event, start, end, resourceId, isAllDay }) => {
    // Reset dragging state
    this.setState({ isDragging: false });

    // Only allow dropping the current appointment
    if (!event.isCurrentAppointment) {
      return;
    }

    const { events = [] } = this.props;

    const dropStart = moment(start);
    const dropEnd = moment(end);

    // Find the target slot - look for slot that contains or starts at the drop time
    const targetSlot = events.find((slot) => {
      if (slot.isCurrentAppointment) return false;

      const slotStart = moment(slot.start);
      const slotEnd = moment(slot.end);

      // Check if drop start time falls within this slot's time range
      // OR if it matches the slot's start time
      const dropFallsInSlot =
        dropStart.isSameOrAfter(slotStart, "minute") &&
        dropStart.isBefore(slotEnd, "minute");
      const dropMatchesSlotStart = dropStart.isSame(slotStart, "minute");

      const timeMatches = dropFallsInSlot || dropMatchesSlotStart;

      // Check resource match - be lenient with null/undefined
      const resourceMatches =
        !resourceId || !slot.resourceId || slot.resourceId === resourceId;

      return timeMatches && resourceMatches;
    });

    // Check if target slot is free
    if (!targetSlot) {
      store.dispatch(
        showToaster(
          intl.formatMessage(Messages.cannot_find_target_slot),
          "error",
          5
        )
      );
      return;
    }

    if (targetSlot.slotStatus !== "FREE") {
      store.dispatch(
        showToaster(
          intl.formatMessage(Messages.cannot_reschedule_occupied_slot),
          "warning",
          5
        )
      );
      return;
    }

    // Call parent handler if provided
    const rescheduleData = {
      appointment: this.props.appointment,
      newStart: start,
      newEnd: end,
      newResourceId: resourceId,
      targetSlot: targetSlot,
    };

    if (this.props.onRescheduleAppointment) {
      this.props.onRescheduleAppointment(rescheduleData);
    }
  };

  // Determine which events can be dragged
  onEventDrag = (event) => {
    return event.isCurrentAppointment === true;
  };

  // Determine which events can be resized
  onEventResize = (event) => {
    return false; // Don't allow resizing
  };

  // Merge current appointment into events list
  getEventsWithCurrentAppointment = () => {
    const { events = [], appointment, resources } = this.props;

    if (!appointment || !appointment.start || !appointment.end) {
      return events;
    }
    let matchedResourceId = null;
    if (
      appointment.participants &&
      Array.isArray(appointment.participants) &&
      resources &&
      Array.isArray(resources)
    ) {
      const resourceIds = resources.map((r) => r.resourceId);
      const matchingParticipant = appointment.participants.find(
        (p) =>
          p?.masterDataValue?.code &&
          resourceIds.includes(p.masterDataValue.code)
      );
      matchedResourceId = matchingParticipant?.masterDataValue?.code;
    }
    const currentAppointmentMarker = {
      id: `current-appointment-${appointment.id || "temp"}`,
      start: moment(appointment.start).toDate(),
      end: moment(appointment.end).toDate(),
      title: "Current Appointment (Drag to Reschedule)",
      slotStatus: "CURRENT_APPOINTMENT",
      slotStatusLabel: "Current Appointment",
      isCurrentAppointment: true,
      resourceId: matchedResourceId || appointment.resourceId || "others",
    };

    return [...events, currentAppointmentMarker];
  };

  // Custom event style for current appointment
  customEventStyleGetter = (event, start, end, isSelected) => {
    const { eventStyleGetter } = this.props;
    const { isDragging } = this.state;

    if (event.isCurrentAppointment) {
      return {
        style: {
          backgroundColor: "#ffc107",
          borderColor: "#ff9800",
          border: "2px dashed #ff9800",
          opacity: 0.8,
          color: "#000",
          fontWeight: "bold",
          borderRadius: "5px",
          cursor: "move",
        },
      };
    }

    // Highlight FREE slots when dragging
    if (isDragging && event.slotStatus === "FREE") {
      return {
        style: {
          backgroundColor: "#00dec4",
          borderColor: "#00c2d1",
          border: "2px solid #00c2d1",
          opacity: 0.9,
          color: "#fff",
          fontWeight: "bold",
          borderRadius: "5px",
          boxShadow: "0 0 10px rgba(0, 222, 196, 0.5)",
          transition: "all 0.2s ease",
        },
      };
    }

    // Dim non-FREE slots when dragging
    if (isDragging && event.slotStatus !== "FREE") {
      const baseStyle = eventStyleGetter
        ? eventStyleGetter(event, start, end, isSelected)
        : {};

      return {
        style: {
          ...baseStyle.style,
          opacity: 0.4,
          filter: "grayscale(50%)",
        },
      };
    }

    if (eventStyleGetter) {
      return eventStyleGetter(event, start, end, isSelected);
    }

    return {};
  };

  render() {
    let isMobile = getMobileOperatingSystem() != "unknown";

    let {
      eventTitleAccessor,
      selectable,
      eventTitleDayAccessor,
      selectedEvent,
      agendaLength,
      views,
      step,
      components,
      currentView = isMobile ? Views.DAY : Views.MONTH,
      currentDate = moment().toDate(),
    } = this.props;
    let { minTime, maxTime, hasNavigated, initialDate } = this.state;

    const calendarDate = hasNavigated ? currentDate : initialDate;
    const allEvents = this.getEventsWithCurrentAppointment();

    return (
      <DndProvider backend={HTML5Backend}>
        <DragAndDropCalendar
          dayLayoutAlgorithm="no-overlap"
          length={agendaLength}
          events={allEvents}
          selectable={selectable}
          onSelectEvent={this.handleSelectEvent}
          onSelectSlot={this.handleSelectSlot}
          views={views || allViews}
          step={step || 15}
          showMultiDayTimes
          date={calendarDate}
          onNavigate={this.handleNavigate}
          onView={this.onView}
          localizer={globalizeLocalizer}
          view={currentView}
          onDrillDown={this.onDrillDown}
          onShowMore={this.onShowMore}
          formats={{
            ...(currentView === Views.MONTH
              ? { eventTimeRangeFormat: () => "" }
              : {}),
            ...this.props.formats,
          }}
          titleAccessor={
            currentView === Views.MONTH
              ? eventTitleAccessor
              : eventTitleDayAccessor
              ? eventTitleDayAccessor
              : null
          }
          min={minTime}
          max={maxTime}
          selected={selectedEvent}
          eventPropGetter={
            currentView !== Views.AGENDA ? this.customEventStyleGetter : null
          }
          rtl={!!I18nProvider.isRTLLocale()}
          scrollToTime={this.props.scrollToTime}
          components={components}
          resources={this.props.resources}
          resourceIdAccessor="resourceId"
          resourceTitleAccessor="resourceTitle"
          // Drag and Drop props
          draggableAccessor={this.onEventDrag}
          resizableAccessor={this.onEventResize}
          onDragStart={this.handleDragStart}
          onEventDrop={this.handleEventDrop}
          messages={{
            today: <Text content={Messages.today} />,
            previous: <Text content={Messages.back} />,
            next: <Text content={Messages.next} />,
            month: <Text content={Messages.month} />,
            week: <Text content={Messages.week} />,
            work_week: <Text content={Messages.work_week} />,
            day: <Text content={Messages.day} />,
            agenda: <Text content={Messages.agenda} />,
            noEventsInRange: this.props.loading ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "20px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <div>Loading events...</div>
                <div style={{ marginTop: "10px", width: "100px" }}>
                  <MPSBarLoader />
                </div>
              </div>
            ) : (
              <Text content={Messages.no_events} />
            ),
            showMore: function showMore(total) {
              return <Text content={Messages.cal_more} data={{ total }} />;
            },
          }}
        />
      </DndProvider>
    );
  }
}

export default MPSCalendar;
